/**
 * RFC 0319 3g: what has happened to a task, and what completes it.
 *
 * The five statuses do not change. What a task could not say before is why it
 * is in one of them. "Implemented" is not a status, it is something that
 * happened, and so are "reviewed", "merged" and "asked". Those are events:
 * appended, never edited, and never copied into the registry, because a list
 * reads the status and only an open task needs the evidence.
 *
 * The completion rule lives here too, so every caller — the agent tools, the
 * app bridge, the editor — gets the same answer to "does this complete now?".
 */

import { cleanRef } from './refs'
import type { Task, TaskCompletion, TaskEvent, TaskRef } from './types'

/** A branch or a PR: the refs that make a task a slice of a build order. */
const DELIVERY_REF_TYPES = new Set(['branch', 'pr'])

/** Evidence that the branch actually landed. */
export const MERGE_EVENT = 'merged'
/** Evidence that the code is written, which is not the same as landed. */
export const IMPLEMENTED_EVENT = 'implemented'

/** A plain event with only defined fields, because Automerge refuses
 *  `undefined`. Null when there is nothing to record. */
export function cleanEvent(event: TaskEvent): TaskEvent | null {
  const type = typeof event?.type === 'string' ? event.type.trim() : ''
  if (!type) return null
  const at =
    typeof event.at === 'string' && event.at.trim()
      ? event.at.trim()
      : new Date().toISOString()
  const by = typeof event.by === 'string' ? event.by.trim() : ''
  const note = typeof event.note === 'string' ? event.note.trim() : ''
  const refs = Array.isArray(event.refs)
    ? event.refs.map(cleanRef).filter((r): r is TaskRef => r !== null)
    : []
  return {
    type,
    at,
    ...(by ? { by } : {}),
    ...(refs.length > 0 ? { refs } : {}),
    ...(note ? { note } : {}),
  }
}

/** A task's events, oldest first. Absent reads as none. */
export function taskEvents(task: Partial<Task>): TaskEvent[] {
  if (!Array.isArray(task.events)) return []
  return task.events.map(cleanEvent).filter((e): e is TaskEvent => e !== null)
}

/**
 * What completes this task. Stored when it is known; otherwise derived, so a
 * task written before S10 answers the same as one written after: a task that
 * names a branch or a PR is a slice, anything else is manual.
 */
export function completionRule(task: Partial<Task>): TaskCompletion {
  if (task.completion === 'merge' || task.completion === 'manual')
    return task.completion
  return defaultCompletion(Array.isArray(task.refs) ? task.refs : [])
}

/** The rule a task with these refs gets at creation. */
export function defaultCompletion(refs: TaskRef[]): TaskCompletion {
  return refs.some((r) => DELIVERY_REF_TYPES.has(r?.type)) ? 'merge' : 'manual'
}

export function isMergeDriven(task: Partial<Task>): boolean {
  return completionRule(task) === 'merge'
}

/**
 * True when the task carries proof that its branch landed: a `merged` event,
 * or a ref with role `closed-by`. Either is written by the merge; a task that
 * has neither has not merged, however finished its code is.
 */
export function hasMergeEvidence(
  task: Partial<Task>,
  extraRefs: TaskRef[] = [],
): boolean {
  if (taskEvents(task).some((e) => e.type === MERGE_EVENT)) return true
  const refs = [...(Array.isArray(task.refs) ? task.refs : []), ...extraRefs]
  return refs.some((r) => r?.role === 'closed-by')
}

/**
 * Whether a completion asked for now may go through, and why not if it may
 * not. `closedBy` is the evidence offered with the request.
 */
export function mayComplete(
  task: Partial<Task>,
  closedBy: TaskRef[] = [],
): { allowed: true } | { allowed: false; reason: string } {
  if (!isMergeDriven(task)) return { allowed: true }
  if (hasMergeEvidence(task, closedBy)) return { allowed: true }
  return {
    allowed: false,
    reason:
      'this task completes on merge (RFC 0319 3g): it stays active, with the work recorded as an implemented event, until its branch merges. Complete it with a closed-by ref for the PR, or set completion to manual if it lands outside a PR.',
  }
}

/**
 * Append `event` to a task inside an Automerge change.
 *
 * `once` drops it when an event of the same type is already there, which is
 * what makes an import and the correction idempotent. Without it every call
 * appends, because two reviews of two commits are two events.
 */
export function appendEvent(
  d: Task,
  event: TaskEvent,
  options?: { once?: boolean },
): TaskEvent | null {
  const clean = cleanEvent(event)
  if (!clean) return null
  if (!Array.isArray(d.events)) d.events = []
  if (options?.once && d.events.some((e) => e.type === clean.type)) return null
  d.events.push(clean)
  return clean
}
